export interface Vehicle {
  id: number;
  unitNumber: string;
  timberlineUnitNumber?: string;
  description?: string;
  status: string;
  numberOfAxles?: number;
  year?: number;
  rate?: number;
  vehicleType?: string;
  vehicleClassification?: string;
  trailerOrAttachment?: string;
  fuelType?: string;
  brakeTypeFront?: string;
  brakeTypeRear?: string;
  hasGPS?: boolean;
  canPatrol?: boolean;
  comments?: string;
  imageUrl?: string;
  currentStatus?: string;
  currentOdometer?: number;
  lastOdometerReportedType?: string;
  lastOdometerReportedDateTime?: string;
  nextPMDue?: number;
  nextCVIP?: string;
  nextPMType?: string;
  nextMinor?: number;
  nextMajor?: number;
  nextGreasing?: number;
  estimatedReturnToServiceDate?: string;
  parkedReady?: boolean;
  lastParked?: string;
  vehicleProfileId?: number;
  vehicleProfile?: VehicleProfile;
  vehicleServiceAreas?: VehicleServiceArea[];
  vehicleHistory?: VehicleHistory[];
  vehicleComponents?: VehicleComponent[];
  blueBookRates?: VehicleBlueBookRate[];
  vehicleAttachments?: VehicleAttachment[];
  // New fields
  vinNumber?: string;
  make?: string;
  model?: string;
  summerConfiguration?: SummerConfiguration;
  winterConfiguration?: WinterConfiguration;
  spreaderSystemType?: string;
  warrantyStatus?: WarrantyStatus;
  registrationNumber?: string;
  tareWeight?: number;
  wheelbase?: number;
  gvwr?: number;
  height?: number;
  length?: number;
  width?: number;
  calculatedCenterOfGravity?: number;
  currentLocation?: string;
  ownershipStatus?: OwnershipStatus;
  createdAt?: string;
  updatedAt?: string;
}

export interface VehicleServiceArea {
  id: number;
  vehicleId: number;
  serviceAreaId: number;
  serviceArea: {
    id: number;
    name: string;
    code?: string;
  };
}

export interface VehicleProfile {
  id: number;
  inspection: string;
  vehicleType?: string;
  vehicleClassification?: string;
  pmClassification?: string;
  odometerAlerts?: string;
  mandatoryOdometerPicture?: boolean;
  requiresCVIExpiryDate?: boolean;
  requiresOdometerReading?: boolean;
  requiresBrakeInspection?: boolean;
  requiresPostTrip?: boolean;
  requiresForemanApproval?: boolean;
  requiresMechSupervisorApproval?: boolean;
  postTripThreshold?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface VehicleHistory {
  id: number;
  vehicleId: number;
  field?: string;
  oldValue?: string;
  newValue?: string;
  comments?: string;
  stage: string;
  external: boolean;
  changedBy?: number;
  createdAt: string;
  changedByUser?: {
    id: number;
    name: string;
    lastName: string;
    email: string;
  };
}

export interface VehicleFilters {
  currentStatus?: string;
  serviceArea?: string;
  /** Server resolves home foreman area to service area for allocation filter (ignored when `search` is set). */
  contextForemanAreaId?: string;
  vehicleClassification?: string;
  search?: string;
  /** Server-side sort (allowlisted): unitNumber, description, vehicleType, vehicleClassification, currentStatus. */
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface VehicleListResponse {
  data: Vehicle[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

export interface VehicleFilterOptions {
  currentStatuses: string[];
  serviceAreas: { id: number; name: string; code?: string }[];
  vehicleClassifications: string[];
}

export interface VehicleProfileFormData {
  inspection: string;
  vehicleType?: string;
  vehicleClassification?: string;
  pmClassification?: string;
  odometerAlerts?: string;
  mandatoryOdometerPicture?: boolean;
  requiresCVIExpiryDate?: boolean;
  requiresOdometerReading?: boolean;
  requiresBrakeInspection?: boolean;
  requiresPostTrip?: boolean;
  requiresForemanApproval?: boolean;
  requiresMechSupervisorApproval?: boolean;
  postTripThreshold?: number;
}

export enum SummerConfiguration {
  PlowWithSolidSpreader = 'PlowWithSolidSpreader',
  PlowWithBrine = 'PlowWithBrine',
  WaterTruck = 'WaterTruck',
  SignTruckWithMobileCrane = 'SignTruckWithMobileCrane',
  BridgeTruckWithMobileCrane = 'BridgeTruckWithMobileCrane'
}

export enum WinterConfiguration {
  Tractor = 'Tractor'
}

export enum WarrantyStatus {
  Active = 'Active',
  Expired = 'Expired'
}

export enum OwnershipStatus {
  Owned = 'Owned',
  Leased = 'Leased',
  Rented = 'Rented'
}

export enum ComponentType {
  Engine = 'Engine',
  Transmission = 'Transmission',
  Differentials = 'Differentials',
  HydraulicPump = 'HydraulicPump'
}

export interface VehicleComponent {
  id: number;
  vehicleId: number;
  serialNumber?: string;
  componentType?: ComponentType;
  createdAt?: string;
  updatedAt?: string;
}

export interface VehicleBlueBookRate {
  id: number;
  vehicleId: number;
  rate: number;
  year: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface VehicleAttachment {
  id: number;
  vehicleId: number;
  name?: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}
